import { ReactElement, useMemo, useState } from 'react';
import { PlanEntitlementKey } from '../../api/graphql/generated';
import { useCurrentUser } from '../../api/user.hooks';
import {
  useHasEntitlement,
  useHasEntitlementLazy,
} from '../plan/plan-entitlement-hooks';
import { useHasAssignedPlanLicenseLazy } from '../plan/plan-hooks';
import { PaywallModal } from './paywall-modal';

interface ChildrenProps {
  presentUpgrade: () => void;
  hasEntitlement: boolean;
}

interface PaywallModalWrapperProps {
  children: (props: ChildrenProps) => ReactElement;
  planEntitlementKey: PlanEntitlementKey;
}

export const PaywallModalWrapper = (
  props: PaywallModalWrapperProps
): ReactElement => {
  const { children, planEntitlementKey } = props;
  const { childrenProps, modal } = usePaywallModal(planEntitlementKey);

  return (
    <>
      {children(childrenProps)}
      {modal}
    </>
  );
};

interface PaywallModalReturn {
  childrenProps: ChildrenProps;
  modal: ReactElement;
}

export const usePaywallModal = (
  planEntitlementKey: PlanEntitlementKey
): PaywallModalReturn => {
  const user = useCurrentUser();
  const [showPaywall, setShowPaywall] = useState(false);
  const hasEntitlement = useHasEntitlement(planEntitlementKey);

  const childrenProps = useMemo((): ChildrenProps => {
    return {
      hasEntitlement,
      presentUpgrade: () => setShowPaywall(true),
    };
  }, [hasEntitlement]);

  return {
    childrenProps,
    modal: (
      <PaywallModal
        user={user}
        planEntitlementKey={planEntitlementKey}
        isOpen={showPaywall}
        onDismiss={() => setShowPaywall(false)}
      />
    ),
  };
};

interface PaywallActionParams {
  planEntitlementKey: PlanEntitlementKey;
  machineId?: string;
  action: () => void;
}

type PaywallActionFn = (params: PaywallActionParams) => void;

/**
 * Wraps an action with a check for entitlements
 */
const usePaywallActionModalLazy = (): {
  paywallAction: PaywallActionFn;
  paywallModal: ReactElement;
} => {
  const user = useCurrentUser();
  const [currentKey, setCurrentKey] = useState<PlanEntitlementKey>();
  const hasEntitlement = useHasEntitlementLazy();
  const hasAssignedPlanLicense = useHasAssignedPlanLicenseLazy();
  const [isOpen, setIsOpen] = useState(false);

  const paywallAction = (params: PaywallActionParams) => {
    const { planEntitlementKey, action, machineId } = params;
    const isTenantEntitled = hasEntitlement(planEntitlementKey);
    const isMachineLicensed = hasAssignedPlanLicense({
      key: planEntitlementKey,
      assignedTo: machineId ?? 'no-machine',
    });

    if (isTenantEntitled && isMachineLicensed) {
      action();
      return;
    }

    setCurrentKey(planEntitlementKey);
    setIsOpen(true);
  };

  return {
    paywallAction,
    paywallModal: (
      <PaywallModal
        user={user}
        planEntitlementKey={currentKey}
        isOpen={isOpen}
        onDismiss={() => {
          setIsOpen(false);
        }}
      />
    ),
  };
};

export { usePaywallActionModalLazy };
